import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { tryoutAPI, resultAPI, subscriptionTierAPI, userSubscriptionAPI, paymentAPI, packageAdminAPI, publicAPI, landingSettingsAPI, purchaseAPI, uploadAPI, materiAPI } from '@/lib/api';
import { formatPrice } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  BookOpen, Clock, FileText, Trophy, LogOut, User, ArrowLeft,
  ChevronRight, Play, History, Package, ArrowRight, Lock,
  Crown, Star, Diamond, CheckCircle, Zap, Loader2, GraduationCap, 
  Eye, TrendingUp, Calendar, Award, Sparkles, Gift, CreditCard,
  Target, BarChart3, Medal, Building2, Copy, Upload, AlertTriangle, Banknote, CalendarCheck, QrCode,
  Download, FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';

// Program data dengan gambar
const PROGRAMS = [
  { id: 'UTBK-SNBT', name: 'UTBK-SNBT', description: 'Ujian Tulis Berbasis Komputer untuk masuk PTN', color: '#4472C4', image: 'https://images.pexels.com/photos/267885/pexels-photo-267885.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { id: 'CPNS', name: 'CPNS', description: 'Calon Pegawai Negeri Sipil', color: '#059669', image: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { id: 'TNI', name: 'TNI/POLRI', description: 'Tentara Nasional Indonesia & Kepolisian', color: '#DC2626', image: 'https://images.pexels.com/photos/8112181/pexels-photo-8112181.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { id: 'POLRI', name: 'POLRI', description: 'Kepolisian Republik Indonesia', color: '#1E40AF', image: 'https://images.pexels.com/photos/8112181/pexels-photo-8112181.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { id: 'SEKOLAH KEDINASAN', name: 'Sekolah Kedinasan', description: 'STAN, STIS, IPDN, dan lainnya', color: '#7C3AED', image: 'https://images.pexels.com/photos/256490/pexels-photo-256490.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { id: 'BUMN', name: 'BUMN', description: 'Badan Usaha Milik Negara', color: '#EA580C', image: 'https://images.pexels.com/photos/273209/pexels-photo-273209.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { id: 'TKA', name: 'TKA', description: 'Tes Kemampuan Akademik', color: '#0D9488', image: 'https://images.pexels.com/photos/4145354/pexels-photo-4145354.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { id: 'SMA Favorit', name: 'SMA Unggul/Favorit', description: 'Persiapan masuk SMA unggulan', color: '#0891B2', image: 'https://images.pexels.com/photos/5212345/pexels-photo-5212345.jpeg?auto=compress&cs=tinysrgb&w=400' },
];

export default function StudentDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tryouts, setTryouts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [results, setResults] = useState([]);
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [userSubscription, setUserSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [activeTab, setActiveTab] = useState('gratis');
  const [purchaseDialog, setPurchaseDialog] = useState({ open: false, item: null, type: null });
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [bankInfo, setBankInfo] = useState(null);
  const [activeEvents, setActiveEvents] = useState([]);
  const [materiList, setMateriList] = useState([]);
  
  // Manual payment form state (same as PackagesPage)
  const [manualPaymentForm, setManualPaymentForm] = useState({
    bank_name: '',
    sender_name: '',
    payment_proof: null,
    payment_proof_url: ''
  });
  const [uploadingProof, setUploadingProof] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Load materi when program is selected
  useEffect(() => {
    if (selectedProgram) {
      loadMateri(selectedProgram.id);
    }
  }, [selectedProgram]);

  const loadMateri = async (program) => {
    try {
      const res = await materiAPI.getForStudent({ program });
      setMateriList(res.data || []);
    } catch (err) {
      console.error('Failed to load materi:', err);
      setMateriList([]);
    }
  };

  const loadData = async () => {
    try {
      const [tiersRes, subRes, tryoutsRes, resultsRes, packagesRes, settingsRes, eventsRes] = await Promise.all([
        subscriptionTierAPI.getPublic().catch(() => ({ data: [] })),
        userSubscriptionAPI.getCurrent().catch(() => ({ data: { subscription: null } })),
        tryoutAPI.getAll(),
        resultAPI.getAll(),
        publicAPI.getPackages().catch(() => ({ data: [] })),
        landingSettingsAPI.getPublic().catch(() => ({ data: {} })),
        publicAPI.getActiveEvents().catch(() => ({ data: [] }))
      ]);
      
      setSubscriptionTiers(tiersRes.data || []);
      setUserSubscription(subRes.data?.subscription);
      setTryouts(tryoutsRes.data || []);
      setResults(resultsRes.data || []);
      setPackages(packagesRes.data || []);
      setActiveEvents(eventsRes.data || []);
      
      // Extract bank info from landing settings
      const settings = settingsRes.data || {};
      if (settings.bank_name && settings.bank_account_number) {
        setBankInfo({
          bank_name: settings.bank_name,
          account_number: settings.bank_account_number,
          account_name: settings.bank_account_name || '',
          note: settings.bank_info_note || ''
        });
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const hasActiveSubscription = userSubscription && new Date(userSubscription.expires_at) > new Date();

  // Calculate user stats
  const totalAttempts = results.length;
  const avgScore = totalAttempts > 0 
    ? Math.round(results.reduce((acc, r) => acc + (r.percentage || 0), 0) / totalAttempts)
    : 0;
  const bestScore = totalAttempts > 0 
    ? Math.max(...results.map(r => r.percentage || 0))
    : 0;

  // Filter tryouts by program and type
  const programTryouts = selectedProgram 
    ? tryouts.filter(t => t.category === selectedProgram.id || t.program === selectedProgram.id)
    : tryouts;
  
  const freeTryouts = programTryouts.filter(t => t.price === 0 || !t.price);
  const paidTryouts = programTryouts.filter(t => t.price > 0);
  const programPackages = selectedProgram 
    ? packages.filter(p => p.program === selectedProgram.id || p.program === selectedProgram.name)
    : packages;

  const handleStartTryout = async (tryout) => {
    // For free tryouts, navigate directly
    if (!tryout.price || tryout.price === 0) {
      navigate(`/exam/${tryout.id}`);
      return;
    }
    
    // For paid tryouts, check access
    try {
      const res = await tryoutAPI.checkAccess(tryout.id);
      if (res.data.has_access) {
        navigate(`/exam/${tryout.id}`);
      } else if (res.data.reason === 'pending_confirmation') {
        toast.info('Menunggu konfirmasi admin. Silakan hubungi admin jika sudah transfer.');
      } else {
        // Show purchase dialog
        handlePurchase(tryout, 'tryout');
      }
    } catch (err) {
      // If check fails, fallback to simple subscription check
      if (hasActiveSubscription) {
        navigate(`/exam/${tryout.id}`);
      } else {
        handlePurchase(tryout, 'tryout');
      }
    }
  };

  const handlePurchase = (item, type) => {
    setPurchaseDialog({ open: true, item, type });
    // Reset form when opening dialog
    setManualPaymentForm({
      bank_name: '',
      sender_name: '',
      payment_proof: null,
      payment_proof_url: ''
    });
  };

  // Handle payment proof upload
  const handlePaymentProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('File harus berupa gambar (JPG, PNG, GIF, WebP)');
      return;
    }

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }

    setUploadingProof(true);
    try {
      const response = await uploadAPI.uploadPaymentProof(file);
      setManualPaymentForm(prev => ({
        ...prev,
        payment_proof: file,
        payment_proof_url: response.data.url
      }));
      toast.success('Bukti pembayaran berhasil diunggah');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Gagal mengunggah bukti pembayaran');
    } finally {
      setUploadingProof(false);
    }
  };

  const confirmPurchase = async () => {
    if (!purchaseDialog.item || !user) {
      toast.error('Data tidak lengkap');
      return;
    }

    if (!manualPaymentForm.payment_proof_url) {
      toast.error('Silakan upload bukti pembayaran');
      return;
    }

    if (!manualPaymentForm.bank_name.trim()) {
      toast.error('Silakan isi nama bank pengirim');
      return;
    }

    if (!manualPaymentForm.sender_name.trim()) {
      toast.error('Silakan isi nama pengirim');
      return;
    }

    setPurchaseLoading(true);
    try {
      await purchaseAPI.request({
        item_type: purchaseDialog.type, // "tryout" or "package"
        item_id: purchaseDialog.item.id,
        payment_proof: manualPaymentForm.payment_proof_url,
        payment_method: 'manual_transfer',
        bank_name: manualPaymentForm.bank_name,
        sender_name: manualPaymentForm.sender_name,
        transfer_amount: parseInt(purchaseDialog.item.price || purchaseDialog.item.final_price) || 0
      });
      
      toast.success('Permintaan pembelian berhasil dikirim! Menunggu konfirmasi admin.');
      setPurchaseDialog({ open: false, item: null, type: null });
      setManualPaymentForm({
        bank_name: '',
        sender_name: '',
        payment_proof: null,
        payment_proof_url: ''
      });
      loadData();
    } catch (err) {
      console.error('Failed to create purchase request:', err);
      toast.error(err.response?.data?.detail || 'Gagal memproses permintaan pembelian');
    } finally {
      setPurchaseLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#4472C4] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-600 font-medium">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50" data-testid="student-dashboard">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo-mytentor.png" alt="MyTentor" className="w-10 h-10 object-contain" />
            <span className="text-xl font-bold text-slate-900 hidden sm:block">MyTentor</span>
          </Link>
          
          <div className="flex items-center gap-2 sm:gap-4">
            {user?.user_code && (
              <Badge variant="outline" className="hidden sm:flex bg-[#4472C4]/10 text-[#4472C4] border-[#4472C4]/30">
                {user.user_code}
              </Badge>
            )}
            <div className="flex items-center gap-2 text-slate-700">
              <div className="w-8 h-8 rounded-full bg-[#4472C4] flex items-center justify-center text-white font-bold text-sm">
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
              <span className="font-medium hidden sm:block">{user?.name}</span>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleLogout}
              className="text-slate-500 hover:text-red-500 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Back Button when program selected */}
        {selectedProgram && (
          <Button 
            variant="ghost" 
            onClick={() => setSelectedProgram(null)}
            className="mb-4 text-slate-600 hover:text-slate-900 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke Pilih Program
          </Button>
        )}

        {!selectedProgram ? (
          <>
            {/* Welcome Banner */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#4472C4] via-[#5a8ad9] to-[#7ba3e8] text-white p-6 sm:p-8 mb-8">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative z-10">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <GraduationCap className="w-10 h-10 text-yellow-300" />
                      <div>
                        <p className="text-white/80 text-sm">Selamat Datang,</p>
                        <h1 className="text-2xl sm:text-3xl font-bold">{user?.name}!</h1>
                      </div>
                    </div>
                    <p className="text-white/80 mt-2">Pilih program dan mulai persiapan ujianmu sekarang</p>
                  </div>
                  
                  {hasActiveSubscription && userSubscription && (
                    <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
                      <Crown className="w-5 h-5 text-yellow-300" />
                      <div className="text-sm">
                        <p className="font-semibold">Premium Active</p>
                        <p className="text-white/80 text-xs">s/d {new Date(userSubscription.expires_at).toLocaleDateString('id-ID')}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                    <Target className="w-6 h-6 mx-auto mb-1 text-yellow-300" />
                    <p className="text-2xl font-bold">{totalAttempts}</p>
                    <p className="text-xs text-white/70">Try Out</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                    <BarChart3 className="w-6 h-6 mx-auto mb-1 text-green-300" />
                    <p className="text-2xl font-bold">{avgScore}%</p>
                    <p className="text-xs text-white/70">Rata-rata</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                    <Medal className="w-6 h-6 mx-auto mb-1 text-orange-300" />
                    <p className="text-2xl font-bold">{bestScore}%</p>
                    <p className="text-xs text-white/70">Skor Tertinggi</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Event Try Outs */}
            {activeEvents.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <CalendarCheck className="w-5 h-5 text-green-600" />
                  Event Try Out Aktif
                  <Badge className="bg-green-100 text-green-700 text-xs ml-2">LIVE</Badge>
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeEvents.map((event) => (
                    <Card 
                      key={event.id}
                      className="group border-2 border-green-200 bg-gradient-to-br from-green-50 to-white hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                      data-testid={`event-card-${event.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <Badge className="bg-green-500 text-white text-xs">Berlangsung</Badge>
                          <span className="text-xs text-slate-500">{event.participant_count} peserta</span>
                        </div>
                        <h3 className="font-bold text-slate-900 text-lg mb-1">{event.title}</h3>
                        <p className="text-sm text-slate-500 mb-3 line-clamp-2">{event.description}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-600 mb-4">
                          <BookOpen className="w-3 h-3" />
                          <span>{event.tryout_title}</span>
                        </div>
                        <Link to="/event-tryout">
                          <Button 
                            className="w-full bg-green-600 hover:bg-green-700"
                            data-testid={`join-event-btn-${event.id}`}
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Ikuti Event
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <div className="mt-3 text-center">
                  <Link to="/event-tryout">
                    <Button variant="outline" size="sm" className="text-green-600 border-green-300 hover:bg-green-50">
                      Masukkan Nomor Peserta
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            {/* Program Selection */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#4472C4]" />
                Pilih Program
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {PROGRAMS.map((program) => {
                  const programTryoutCount = tryouts.filter(t => t.category === program.id || t.program === program.id).length;
                  return (
                    <Card 
                      key={program.id}
                      className="group cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden border-0 shadow-md"
                      onClick={() => setSelectedProgram(program)}
                      data-testid={`program-card-${program.id}`}
                    >
                      <div className="h-28 sm:h-32 bg-cover bg-center relative" style={{ backgroundImage: `url(${program.image})` }}>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                        <Badge 
                          className="absolute top-3 left-3 text-white border-0 text-xs" 
                          style={{ backgroundColor: program.color }}
                        >
                          {program.id}
                        </Badge>
                        <div className="absolute bottom-3 left-3 right-3 text-white">
                          <h3 className="font-bold text-lg">{program.name}</h3>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <p className="text-sm text-slate-500 line-clamp-2">{program.description}</p>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                          <span className="text-sm font-medium text-[#4472C4]">
                            {programTryoutCount} Try Out
                          </span>
                          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#4472C4] group-hover:translate-x-1 transition-all" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Recent Results */}
            {results.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <History className="w-5 h-5 text-[#4472C4]" />
                  Riwayat Try Out Terakhir
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {results.slice(0, 6).map((result) => (
                    <Card key={result.id} className="hover:shadow-md transition-shadow border-0 shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <h4 className="font-semibold text-slate-900 line-clamp-2 text-sm">{result.tryout_title}</h4>
                          <Badge 
                            className={`shrink-0 ${result.percentage >= 70 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}
                          >
                            {result.percentage}%
                          </Badge>
                        </div>
                        <Progress value={result.percentage} className="h-2 mb-3" />
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>Skor: {result.score}/{result.total_questions * 10}</span>
                          <Link to={`/result/${result.id}`}>
                            <Button variant="link" size="sm" className="p-0 h-auto text-[#4472C4]">
                              Detail <Eye className="w-3 h-3 ml-1" />
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          /* Program Detail View with Tabs */
          <div>
            {/* Program Header */}
            <div className="relative overflow-hidden rounded-2xl mb-6" style={{ backgroundColor: selectedProgram.color }}>
              <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${selectedProgram.image})` }} />
              <div className="relative z-10 p-6 text-white">
                <Badge className="bg-white/20 text-white border-0 mb-3">{selectedProgram.id}</Badge>
                <h1 className="text-2xl sm:text-3xl font-bold mb-2">{selectedProgram.name}</h1>
                <p className="text-white/80">{selectedProgram.description}</p>
                <div className="flex gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-1">
                    <Gift className="w-4 h-4" />
                    <span>{freeTryouts.length} Gratis</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CreditCard className="w-4 h-4" />
                    <span>{paidTryouts.length} Berbayar</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Package className="w-4 h-4" />
                    <span>{programPackages.length} Paket</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {[
                { id: 'gratis', label: 'Try Out Gratis', icon: Gift, count: freeTryouts.length },
                { id: 'berbayar', label: 'Try Out Berbayar', icon: CreditCard, count: paidTryouts.length },
                { id: 'materi', label: 'Materi Belajar', icon: FolderOpen, count: materiList.length },
                { id: 'paket', label: 'Paket Bundling', icon: Package, count: programPackages.length },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                    activeTab === tab.id 
                      ? 'bg-[#4472C4] text-white shadow-lg shadow-[#4472C4]/30' 
                      : 'bg-white text-slate-600 hover:bg-slate-100 shadow-sm'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                  <Badge className={`${activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'} text-xs`}>
                    {tab.count}
                  </Badge>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'gratis' && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {freeTryouts.length === 0 ? (
                  <Card className="col-span-full py-12 text-center border-dashed">
                    <Gift className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">Belum ada Try Out gratis untuk program ini</p>
                  </Card>
                ) : (
                  freeTryouts.map(tryout => (
                    <TryoutCard 
                      key={tryout.id} 
                      tryout={tryout} 
                      isFree={true}
                      onStart={() => handleStartTryout(tryout)}
                      results={results}
                    />
                  ))
                )}
              </div>
            )}

            {activeTab === 'berbayar' && (
              <div>
                {!hasActiveSubscription && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
                    <Lock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Konten Premium</p>
                      <p className="text-sm text-amber-700">Berlangganan atau beli paket untuk mengakses Try Out berbayar</p>
                    </div>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {paidTryouts.length === 0 ? (
                    <Card className="col-span-full py-12 text-center border-dashed">
                      <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p className="text-slate-500">Belum ada Try Out berbayar untuk program ini</p>
                    </Card>
                  ) : (
                    paidTryouts.map(tryout => (
                      <TryoutCard 
                        key={tryout.id} 
                        tryout={tryout} 
                        isFree={false}
                        locked={!hasActiveSubscription}
                        onStart={() => handleStartTryout(tryout)}
                        onBuy={() => handlePurchase(tryout, 'tryout')}
                        results={results}
                      />
                    ))
                  )}
                </div>
              </div>
            )}

            {activeTab === 'materi' && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {materiList.length === 0 ? (
                  <Card className="col-span-full py-12 text-center border-dashed">
                    <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">Belum ada materi untuk program {selectedProgram?.name}</p>
                    <p className="text-sm text-slate-400 mt-1">Admin akan segera menambahkan materi belajar</p>
                  </Card>
                ) : (
                  materiList.map(materi => (
                    <MateriCard key={materi.id} materi={materi} />
                  ))
                )}
              </div>
            )}

            {activeTab === 'paket' && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {programPackages.length === 0 ? (
                  <Card className="col-span-full py-12 text-center border-dashed">
                    <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">Belum ada paket bundling untuk program ini</p>
                    <Link to="/packages">
                      <Button variant="outline" className="mt-4">
                        Lihat Semua Paket
                      </Button>
                    </Link>
                  </Card>
                ) : (
                  programPackages.map(pkg => (
                    <PackageCard 
                      key={pkg.id} 
                      pkg={pkg}
                      onBuy={() => handlePurchase(pkg, 'package')}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Purchase Dialog - Manual Transfer (Same as PackagesPage) */}
      <Dialog open={purchaseDialog.open} onOpenChange={(o) => !o && setPurchaseDialog({ open: false, item: null, type: null })}>
        <DialogContent className="max-w-sm max-h-[90vh] flex flex-col">
          <DialogHeader className="pb-1 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Banknote className="w-4 h-4 text-amber-600" />
              Transfer Bank
            </DialogTitle>
          </DialogHeader>
          
          {purchaseDialog.item && (
            <div className="space-y-2 text-xs overflow-y-auto flex-1 pr-1">
              {/* Item Info - Compact */}
              <div className="bg-slate-50 p-2 rounded-lg">
                <h3 className="font-semibold text-slate-900 text-sm truncate">{purchaseDialog.item.title || purchaseDialog.item.name}</h3>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-slate-500">Total</span>
                  <span className="text-base font-bold text-[#4472C4]">
                    {formatPrice(purchaseDialog.item.price || purchaseDialog.item.final_price)}
                  </span>
                </div>
              </div>

              {/* Bank Transfer & QRIS - Stacked on small mobile, side by side on larger screens */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {/* Bank Transfer Info - More Compact with overflow handling */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-1.5">
                  <div className="flex items-center gap-1 text-blue-800 font-medium mb-1 text-[10px]">
                    <Building2 className="w-3 h-3 shrink-0" />
                    <span>Transfer ke:</span>
                  </div>
                  <div className="space-y-0.5 text-[9px]">
                    {[
                      { bank: 'BCA', no: '1950947191' },
                      { bank: 'BNI', no: '0340100950' },
                      { bank: 'Mandiri', no: '106-00-1301771-3' },
                      { bank: 'BRI', no: '0336-01-092728-50-2' },
                      { bank: 'E-Wallet', no: '081262017001' },
                    ].map((acc) => (
                      <div key={acc.bank} className="flex items-center justify-between bg-white px-1 py-0.5 rounded overflow-hidden">
                        <span className="text-slate-600 font-medium shrink-0">{acc.bank}</span>
                        <div className="flex items-center gap-0.5 min-w-0">
                          <span className="font-mono text-blue-700 text-[8px] truncate">{acc.no}</span>
                          <button 
                            onClick={() => {
                              navigator.clipboard.writeText(acc.no);
                              toast.success(`${acc.bank} disalin!`);
                            }}
                            className="p-0.5 hover:bg-blue-100 rounded shrink-0"
                          >
                            <Copy className="w-2.5 h-2.5 text-blue-700" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-center text-[8px] text-blue-800 font-medium mt-1 pt-1 border-t border-blue-200">
                    a.n. Togi Parsaoran S. Pasaribu, S.Pd
                  </p>
                </div>

                {/* QRIS Payment - Smaller */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-1.5">
                  <div className="flex items-center gap-1 text-emerald-800 font-medium mb-1 text-[10px]">
                    <QrCode className="w-3 h-3 shrink-0" />
                    <span>QRIS</span>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/images/qris-mytentor.png" 
                      alt="QRIS MyTentor" 
                      className="w-20 h-20 sm:w-24 sm:h-24 object-contain"
                    />
                  </div>
                  <p className="text-center text-[8px] text-emerald-700 mt-0.5">
                    GoPay, OVO, DANA, dll
                  </p>
                </div>
              </div>

              {/* Payment Form - Compact */}
              <div className="space-y-1.5">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-0.5">
                    <Label className="text-[10px]">Bank Pengirim *</Label>
                    <Input
                      placeholder="BCA, Mandiri..."
                      value={manualPaymentForm.bank_name}
                      onChange={(e) => setManualPaymentForm(prev => ({ ...prev, bank_name: e.target.value }))}
                      className="h-7 text-xs"
                      data-testid="input-bank-name"
                    />
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[10px]">Nama Pengirim *</Label>
                    <Input
                      placeholder="Nama rekening"
                      value={manualPaymentForm.sender_name}
                      onChange={(e) => setManualPaymentForm(prev => ({ ...prev, sender_name: e.target.value }))}
                      className="h-7 text-xs"
                      data-testid="input-sender-name"
                    />
                  </div>
                </div>

                {/* Upload Payment Proof - More Compact */}
                <div className="space-y-0.5">
                  <Label className="text-[10px]">Bukti Transfer *</Label>
                  <div className="border border-dashed border-slate-300 rounded p-1.5 text-center hover:border-amber-400 transition-colors">
                    {manualPaymentForm.payment_proof_url ? (
                      <div className="flex items-center justify-center gap-2">
                        <img 
                          src={manualPaymentForm.payment_proof_url} 
                          alt="Bukti" 
                          className="h-12 rounded border"
                        />
                        <div className="text-left">
                          <p className="text-[10px] text-green-600 flex items-center gap-0.5">
                            <CheckCircle className="w-3 h-3" />
                            Berhasil
                          </p>
                          <button
                            onClick={() => setManualPaymentForm(prev => ({ ...prev, payment_proof: null, payment_proof_url: '' }))}
                            className="text-[9px] text-red-500 hover:underline"
                          >
                            Hapus
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="cursor-pointer flex items-center justify-center gap-2 py-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePaymentProofUpload}
                          className="hidden"
                          disabled={uploadingProof}
                          data-testid="input-payment-proof"
                        />
                        {uploadingProof ? (
                          <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-4 h-4 text-amber-600" />
                            <span className="text-[10px] text-slate-600">Upload bukti</span>
                          </>
                        )}
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-[9px] text-amber-700 bg-amber-50 p-1 rounded flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                Admin verifikasi maks 1x24 jam
              </p>
            </div>
          )}
          
          <DialogFooter className="gap-1 pt-2 flex-shrink-0 border-t mt-2">
            <Button 
              variant="outline" 
              size="sm"
              className="h-8 text-xs"
              onClick={() => setPurchaseDialog({ open: false, item: null, type: null })}
            >
              Batal
            </Button>
            <Button 
              size="sm"
              className="h-8 text-xs bg-[#4472C4] hover:bg-[#3861a8]"
              onClick={confirmPurchase} 
              disabled={purchaseLoading || !manualPaymentForm.payment_proof_url}
              data-testid="submit-payment-btn"
            >
              {purchaseLoading ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Memproses...
                </>
              ) : (
                <>
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Kirim
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Tryout Card Component
function TryoutCard({ tryout, isFree, locked, onStart, onBuy, results }) {
  const hasAttempted = results?.some(r => r.tryout_id === tryout.id);
  const bestResult = results?.filter(r => r.tryout_id === tryout.id).sort((a, b) => b.percentage - a.percentage)[0];
  
  return (
    <Card className="group hover:shadow-lg transition-all border-0 shadow-sm overflow-hidden">
      <div className={`h-2 ${isFree ? 'bg-green-500' : 'bg-amber-500'}`} />
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3 className="font-semibold text-slate-900 line-clamp-2">{tryout.title}</h3>
          {isFree ? (
            <Badge className="bg-green-100 text-green-700 shrink-0">GRATIS</Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-700 shrink-0">{formatPrice(tryout.price)}</Badge>
          )}
        </div>
        
        <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
          <span className="flex items-center gap-1">
            <FileText className="w-3.5 h-3.5" />
            {tryout.question_count || 0} Soal
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {tryout.duration || 60} Menit
          </span>
        </div>
        
        {hasAttempted && bestResult && (
          <div className="bg-slate-50 rounded-lg p-2 mb-3 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Skor Terbaik</span>
              <span className={`font-bold ${bestResult.percentage >= 70 ? 'text-green-600' : 'text-orange-600'}`}>
                {bestResult.percentage}%
              </span>
            </div>
          </div>
        )}
        
        {locked ? (
          <Button 
            onClick={onBuy} 
            className="w-full bg-amber-500 hover:bg-amber-600 text-white"
          >
            <Lock className="w-4 h-4 mr-2" />
            Beli Akses
          </Button>
        ) : (
          <Button 
            onClick={onStart}
            className="w-full bg-[#4472C4] hover:bg-[#3861a8]"
          >
            <Play className="w-4 h-4 mr-2" />
            {hasAttempted ? 'Kerjakan Lagi' : 'Mulai Try Out'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// Package Card Component
function PackageCard({ pkg, onBuy }) {
  const hasDiscount = pkg.original_price && pkg.original_price > pkg.price;
  
  return (
    <Card className="group hover:shadow-xl transition-all border-0 shadow-md overflow-hidden">
      <div className="h-2 bg-gradient-to-r from-[#4472C4] to-purple-500" />
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-bold text-slate-900">{pkg.title || pkg.name}</h3>
          {hasDiscount && (
            <Badge className="bg-red-100 text-red-600">DISKON</Badge>
          )}
        </div>
        
        <p className="text-sm text-slate-500 line-clamp-2 mb-4">{pkg.description}</p>
        
        <div className="space-y-2 mb-4">
          {pkg.tryout_count && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <FileText className="w-4 h-4 text-[#4472C4]" />
              <span>{pkg.tryout_count} Try Out</span>
            </div>
          )}
          {pkg.duration_days && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Calendar className="w-4 h-4 text-[#4472C4]" />
              <span>Akses {pkg.duration_days} Hari</span>
            </div>
          )}
        </div>
        
        <div className="flex items-end justify-between pt-4 border-t border-slate-100">
          <div>
            {hasDiscount && (
              <span className="text-sm text-slate-400 line-through">{formatPrice(pkg.original_price)}</span>
            )}
            <p className="text-xl font-bold text-[#4472C4]">{formatPrice(pkg.price || pkg.final_price)}</p>
          </div>
          <Button 
            onClick={onBuy}
            className="bg-[#4472C4] hover:bg-[#3861a8]"
          >
            Beli <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Materi Card Component - for learning materials
function MateriCard({ materi }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  const getFileIcon = (filename) => {
    if (!filename) return FileText;
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return FileText;
    if (['doc', 'docx'].includes(ext)) return FileText;
    if (ext === 'csv') return FileText;
    return FileText;
  };
  
  const getFileTypeLabel = (filename) => {
    if (!filename) return 'File';
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'PDF';
    if (['doc', 'docx'].includes(ext)) return 'WORD';
    if (ext === 'csv') return 'CSV';
    return ext?.toUpperCase() || 'File';
  };
  
  const FileIcon = getFileIcon(materi.file_url);
  
  return (
    <Card className="group hover:shadow-lg transition-all border-0 shadow-sm overflow-hidden" data-testid={`materi-card-${materi.id}`}>
      <div className="h-2 bg-gradient-to-r from-emerald-500 to-teal-500" />
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3 className="font-semibold text-slate-900 line-clamp-2">{materi.title}</h3>
          <Badge className="bg-emerald-100 text-emerald-700 shrink-0">
            {getFileTypeLabel(materi.file_url)}
          </Badge>
        </div>
        
        {materi.description && (
          <p className="text-sm text-slate-500 line-clamp-2 mb-3">{materi.description}</p>
        )}
        
        <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
          {materi.subject && (
            <span className="flex items-center gap-1">
              <BookOpen className="w-3.5 h-3.5" />
              {materi.subject}
            </span>
          )}
          {materi.created_at && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(materi.created_at).toLocaleDateString('id-ID')}
            </span>
          )}
        </div>
        
        <a 
          href={materi.file_url?.startsWith('http') ? materi.file_url : `${BACKEND_URL}${materi.file_url}`}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
            <Download className="w-4 h-4 mr-2" />
            Download Materi
          </Button>
        </a>
      </CardContent>
    </Card>
  );
}
